/**
 * Role Management Component
 *
 * Comprehensive role management with permissions
 * Features:
 * - Create/Edit/Delete roles
 * - Assign permissions to roles
 * - View users assigned to each role
 * - Role hierarchies
 */

import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Divider,
  Checkbox,
  Row,
  Col,
  Badge,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { get_roles, add_role, delete_role } from '@/utils/api';
import { accessControlUtils } from '@/components/AccessControlUtils';
import DataTable from '@/components/DataTable';
import { EmptyState } from '@/components/PageHeader';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import HeaderActions from '@/components/HeaderActions';

const { Option } = Select;
const { TextArea } = Input;

// Available permissions (customize based on your system)
const AVAILABLE_PERMISSIONS = [
  { category: "User Management", permissions: ["create_user", "edit_user", "delete_user", "view_users"] },
  { category: "BMR Management", permissions: ["create_bmr", "edit_bmr", "approve_bmr", "release_bmr", "view_bmr"] },
  { category: "E-Signature", permissions: ["sign_documents", "view_signatures", "verify_signatures"] },
  { category: "Configuration", permissions: ["manage_roles", "manage_departments", "manage_designations", "system_config"] },
  { category: "Reports", permissions: ["view_reports", "export_reports", "create_reports"] },
  { category: "Quality Control", permissions: ["qc_approve", "qc_review", "qc_reject"] },
];

const RoleManagement = ({ active = true }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  const access = accessControlUtils("User Management");

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      await get_roles((res) => {
        if (res.data && res.data.data) {
          setRoles(res.data.data);
        }
      });
    } catch (error) {
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role,
      description: role.description,
      permissions: role.permissions || [],
      is_active: role.is_active !== false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (roleId) => {
    try {
      await delete_role({ id: roleId }, (res) => {
        if (res.status === "success") {
          toast.success("Role deleted successfully");
          loadRoles();
        } else {
          toast.error(res.message || "Failed to delete role");
        }
      });
    } catch (error) {
      toast.error("Failed to delete role");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const roleData = {
        role_name: values.role_name,
        description: values.description,
        permissions: values.permissions || [],
        is_active: values.is_active !== false,
        ...(editingRole && { id: editingRole.role_id }),
      };

      await add_role(roleData, (res) => {
        if (res.status === "success") {
          toast.success(editingRole ? "Role updated successfully" : "Role created successfully");
          setModalVisible(false);
          form.resetFields();
          loadRoles();
        } else {
          toast.error(res.message || "Failed to save role");
        }
      });
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };



  const columns = [
    {
      title: "Role Name",
      dataIndex: "role",
      key: "role",
      width: 300,
      render: (text, record) => (
        <Space>
          <SafetyOutlined style={{ color: defaultColors.primary }} />
          <strong>{text}</strong>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
          {record.role_type && (
            <Tooltip title="A system role that approval gating and notifications depend on — always present by default, and cannot be deleted. The name can still be changed freely.">
              <Tag color="gold">System Role</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 320,
      render: (text) => text || <span style={{ color: "#999" }}>No description</span>,
    },
    {
      title: "Permissions",
      key: "permissions",
      width: 360,
      render: (_, record) => {
        const permissions = record.permissions || [];
        const displayPermissions = permissions.slice(0, 3);
        const remainingCount = permissions.length - 3;

        return (
          <Space wrap>
            {displayPermissions.map((perm, i) => (
              <Tag key={i} style={{ fontSize: 11 }}>
                {perm.replace(/_/g, " ")}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Tooltip title={permissions.slice(3).join(", ")}>
                <Tag color="default">+{remainingCount} more</Tag>
              </Tooltip>
            )}
            {permissions.length === 0 && (
              <span style={{ color: "#999" }}>No permissions</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Users",
      key: "users",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Number of users with this role">
          <Badge count={record.user_count || 0} showZero>
            <UserOutlined style={{ fontSize: 18, color: defaultColors.primary }} />
          </Badge>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      align: "center",
      render: (_, record) => (
        <Space>
          {access?.update_access && (
            <Button
              size="small"
              aria-label={`Edit ${record.role}`}
              icon={<Icons.Pencil className="w-3.5 h-3.5 text-amber-600" />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {access?.delete_access && (
            record.role_type ? (
              <Tooltip title="System role — always present by default, cannot be deleted. Approval gating and notifications depend on it.">
                <Button size="small" disabled icon={<Icons.Trash2 className="w-3.5 h-3.5" />}>
                  Delete
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                title="Delete this role?"
                description="Users with this role will lose their permissions."
                onConfirm={() => handleDelete(record.role_id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  className: "bg-red-600 text-white hover:bg-red-700",
                }}
              >
                <Button size="small" danger aria-label={`Delete ${record.role}`} icon={<Icons.Trash2 className="w-3.5 h-3.5" />}>
                  Delete
                </Button>
              </Popconfirm>
            )
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* hosted in User Management: Add Role goes in that page's header, only while this tab is open */}
      {active && access?.create_access && (
        <HeaderActions>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Role
          </Button>
        </HeaderActions>
      )}
      <DataTable
        tableKey="roleManagementTable"
        searchable
        searchKeys={['role', 'description']}
        searchPlaceholder="Search roles…"
        columnConfig
        showCount={false}
        exportable
        exportFilename="roles"
        bordered
        className="role-management-table"
        data={roles}
        columns={columns}
        rowKey="role_id"
        loading={loading}
        scrollX="max-content"
        height={null}
        locale={{
          emptyText: (
            <EmptyState
              title="No roles yet"
              description="Create roles to group permissions and assign them to users."
              actionLabel={access?.create_access ? 'Add Role' : undefined}
              onAction={handleAdd}
            />
          ),
        }}
      />

      <PageModal
        open={modalVisible}
        icon={<SafetyOutlined style={{ fontSize: 22 }} />}
        title={editingRole ? "Edit Role" : "Create New Role"}
        description="A role groups permissions that can be assigned to users."
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={handleSubmit}>
              {editingRole ? "Update" : "Create"}
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="role_name"
            label="Role Name"
            rules={[
              { required: true, message: "Please enter role name" },
              { min: 3, message: "Role name must be at least 3 characters" },
            ]}
          >
            <Input placeholder="e.g., Production Manager, QC Analyst" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe the responsibilities and purpose of this role..."
            />
          </Form.Item>

          <Divider>Permissions</Divider>

          <Form.Item
            name="permissions"
            label="Select Permissions"
          >
            <Checkbox.Group style={{ width: "100%" }}>
              {AVAILABLE_PERMISSIONS.map((category, idx) => (
                <div key={idx} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: defaultColors.primary }}>
                    {category.category}
                  </div>
                  <Row>
                    {category.permissions.map((perm) => (
                      <Col span={12} key={perm} style={{ marginBottom: 8 }}>
                        <Checkbox value={perm}>
                          {perm.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="is_active"
            valuePropName="checked"
          >
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </PageModal>
    </div>
  );
};

export default RoleManagement;
